import { useEffect } from 'react';
import { FlexColumn } from 'components/layout/FlexColumn/FlexColumn';
import {
  SidebarTitle,
  SidebarSectionLabel,
  SidebarClearAllFooter,
} from 'components/layout/SidebarLayout';
import CityFilter from 'components/widgets/DocumentSearch/Filters/CityFilter';
import DateCreatedFilter from 'components/widgets/DocumentSearch/Filters/DateCreatedFilter';
import DateUpdatedFilter from 'components/widgets/DocumentSearch/Filters/DateUpdatedFilter';
import DocumentTypeFilter from 'components/widgets/DocumentSearch/Filters/DocumentTypeFilter';
import LocationFilter from 'components/widgets/DocumentSearch/Filters/LocationFilter';
import StateFilter from 'components/widgets/DocumentSearch/Filters/StateFilter';
import { FilterContextProvider } from 'components/widgets/FilterToolbar/FilterContext';
import { useAvailableCities } from 'hooks/useUserCityAccess';
import {
  useDateUpdatedFilter,
  useResetFilters,
  useStateDocFilter,
} from 'state/filterDocuments';
import { State } from 'utils/constants/location';
import { QUERY_PARAM } from 'utils/constants/QueryParameters';

const containerStyle = {
  height: '100%',
  width: '100%',
};

const scrollStyle = {
  overflowY: 'auto',
  width: '100%',
  flex: 1,
};

// Aligns the date filters with the dropdown rows above and gives them room to
// breathe, matching the sidebar's other sections.
const dateRangeStyle = {
  gap: 'padding_12',
  paddingX: 'padding_8',
  paddingY: 'padding_12',
};

const DocumentSearchSidebarFilters = () => {
  const { stateFilter, setStateFilter } = useStateDocFilter();
  const { availableStates } = useAvailableCities();
  const { resetFilters } = useResetFilters();
  const { dateUpdatedFilter, setDateUpdatedFilter } = useDateUpdatedFilter();

  useEffect(() => {
    if (stateFilter || !availableStates) return;

    // Skip the default when the URL carries saved filter state, so a shared
    // selection (including an intentionally unset state) is respected.
    const hasUrlFilters = new URLSearchParams(window.location.search).has(
      QUERY_PARAM.filters,
    );
    if (hasUrlFilters) return;

    if (availableStates[State.TX]) {
      setStateFilter(State.TX);
    } else if (availableStates[State.FL]) {
      setStateFilter(State.FL);
    } else {
      const firstState = Object.keys(availableStates)[0];
      if (firstState) setStateFilter(firstState);
    }
  }, [availableStates]);

  useEffect(() => {
    // Default the "date updated" filter to the last 3 years, unless the URL
    // carries saved filter state (a shared/refreshed selection is respected).
    const hasUrlFilters = new URLSearchParams(window.location.search).has(
      QUERY_PARAM.filters,
    );
    if (!dateUpdatedFilter && !hasUrlFilters) {
      setDateUpdatedFilter('3_years_ago');
    }
  }, []);

  return (
    <FlexColumn sx={containerStyle}>
      <SidebarTitle>Filing Search</SidebarTitle>
      <FilterContextProvider>
        <FlexColumn sx={scrollStyle}>
          <SidebarSectionLabel label="Location" isFirst />
          <StateFilter />
          <LocationFilter />
          <CityFilter />
          <SidebarSectionLabel label="Filing" />
          <DocumentTypeFilter />
          <SidebarSectionLabel label="Date range" />
          <FlexColumn sx={dateRangeStyle}>
            <DateCreatedFilter />
            <DateUpdatedFilter />
          </FlexColumn>
        </FlexColumn>
      </FilterContextProvider>
      <SidebarClearAllFooter onClick={resetFilters} />
    </FlexColumn>
  );
};

export default DocumentSearchSidebarFilters;
